import { useEffect } from 'react';
import { create } from 'zustand';

interface SWMessage {
  type: 'TICK' | 'COMPLETE' | 'PONG' | 'ERROR';
  remainingMs?: number | null;
  startedAt?: number | null;
  durationMs?: number | null;
  sessionId?: string | null;
  message?: string;
}

interface PendingRequest<T> {
  resolve: (value: T) => void;
  reject: (err: Error) => void;
}

type Listener = (msg: SWMessage) => void;

class FocusController {
  private listeners = new Set<Listener>();
  private sw: ServiceWorker | null = null;
  private activeSessionId: string | null = null;
  private durationMs = 0;
  private startedAt = 0;
  private tickInterval: number | null = null;
  private swReady: Promise<ServiceWorker | null>;
  private pingPending: PendingRequest<SWMessage> | null = null;

  constructor() {
    this.swReady = this.init();
  }

  private async init(): Promise<ServiceWorker | null> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return null;
    }
    try {
      const reg = await navigator.serviceWorker.register('/sw-focus.js', { scope: '/' });
      await navigator.serviceWorker.ready;
      this.sw = reg.active || reg.installing || reg.waiting;
      navigator.serviceWorker.addEventListener('message', (event) => {
        const data = event.data as SWMessage;
        if (data.type === 'PONG' && this.pingPending) {
          this.pingPending.resolve(data);
          this.pingPending = null;
          return;
        }
        this.listeners.forEach((cb) => cb(data));
        if (data.type === 'COMPLETE') {
          this.stopTick();
        }
      });
      return this.sw;
    } catch (err) {
      console.error('Service Worker registration failed', err);
      return null;
    }
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  getActiveSession() {
    return { sessionId: this.activeSessionId, startedAt: this.startedAt, durationMs: this.durationMs };
  }

  async ping(): Promise<SWMessage | null> {
    const sw = await this.swReady;
    if (!sw) return null;
    if (this.pingPending) return null;
    return new Promise((resolve, reject) => {
      this.pingPending = { resolve, reject };
      const timeout = window.setTimeout(() => {
        if (this.pingPending) {
          this.pingPending = null;
          resolve(null);
        }
      }, 1500);
      const orig = this.pingPending!.resolve;
      this.pingPending!.resolve = (value) => {
        window.clearTimeout(timeout);
        orig(value);
      };
      sw.postMessage({ type: 'PING' });
    });
  }

  async start(sessionId: string, durationMinutes: number, opts: { startedAt?: number; durationMs?: number } = {}) {
    const sw = await this.swReady;
    if (!sw) return;
    const durationMs = opts.durationMs ?? durationMinutes * 60_000;
    const startedAt = opts.startedAt ?? Date.now();
    this.activeSessionId = sessionId;
    this.durationMs = durationMs;
    this.startedAt = startedAt;
    sw.postMessage({ type: 'START', sessionId, startedAt, durationMs });
    this.startTick();
  }

  async cancel() {
    const sw = await this.swReady;
    if (sw) sw.postMessage({ type: 'CANCEL' });
    this.stopTick();
    this.activeSessionId = null;
    this.startedAt = 0;
    this.durationMs = 0;
  }

  private startTick() {
    this.stopTick();
    this.tickInterval = window.setInterval(() => {
      if (!this.startedAt || !this.activeSessionId) return;
      const remaining = Math.max(0, this.durationMs - (Date.now() - this.startedAt));
      this.listeners.forEach((cb) => cb({ type: 'TICK', remainingMs: remaining, sessionId: this.activeSessionId }));
      if (remaining <= 0) {
        this.stopTick();
        this.listeners.forEach((cb) => cb({ type: 'COMPLETE', sessionId: this.activeSessionId }));
        this.activeSessionId = null;
      }
    }, 250);
  }

  private stopTick() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }
}

export const focusController = new FocusController();

export function useFocusController() {
  useEffect(() => {
    const unsub = focusController.subscribe(() => {
      // listeners manage their own state via zustand
    });
    return unsub;
  }, []);
  return focusController;
}

interface SWStatus {
  registered: boolean;
  error: string | null;
}

export const useSWStatus = create<SWStatus>(() => ({ registered: false, error: null }));

export function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    useSWStatus.setState({ error: 'Service workers not supported' });
    return;
  }
  navigator.serviceWorker
    .register('/sw-focus.js', { scope: '/' })
    .then(() => {
      useSWStatus.setState({ registered: true });
    })
    .catch((err) => {
      useSWStatus.setState({ error: String(err) });
    });
}
